'use client'

import { Anchor, Box, Flex, Group, Text } from '@mantine/core'
import { sortBy } from 'es-toolkit'
import { DataTable } from 'mantine-datatable'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import NextBaseImage from '@/components/basic/BaseImage'
import PriceChange from '@/components/PriceChange'
import { NOT_APPLICABLE } from '@/configs'
import type { CryptoBasket } from '@/types/invest'
import { getCdcWebSignupLink, gotoCdcWebSignupPage } from '@/utils'

import sharedStyles from '../style.module.css'
import styles from './style.module.css'

const BASKET_PERIOD = '3 Years'
const MAX_COINS = 5

// "Trade" text link → cdc-web signup（同 stocks basket，整行点击与链接行为一致）。
function TradeColumn() {
  const t = useTranslations('coinTable.row')
  return (
    <Anchor href={getCdcWebSignupLink()} onClick={e => e.stopPropagation()} underline="never">
      <Text variant="subtitle-emphasis" c="var(--content-active)" style={{ wordBreak: 'keep-all' }}>
        {t('trade')}
      </Text>
    </Anchor>
  )
}

function CoinsColumn({ basket }: { basket: CryptoBasket }) {
  const sortedCoins = basket.coins
    .slice(0, MAX_COINS)
    .sort((a, b) => a.symbol.localeCompare(b.symbol))
  const remainCount = Math.max(0, basket.coins.length - MAX_COINS)

  return (
    <Flex align="center" justify="center" wrap="nowrap" w="100%">
      {sortedCoins.map((coin, index) => (
        <Box key={coin.id} style={{ marginLeft: index > 0 ? -4 : 0 }}>
          <NextBaseImage
            src={coin.icon_url}
            alt={coin.symbol}
            width="20px"
            height="20px"
            className={styles.basketCoinImage}
          />
        </Box>
      ))}
      {remainCount > 0 && (
        <Text variant="body" c="var(--content-secondary)" ml={6}>
          +{remainCount}
        </Text>
      )}
    </Flex>
  )
}

interface BasketsTableProps {
  baskets: CryptoBasket[]
}

export default function BasketsTable({ baskets }: BasketsTableProps) {
  const t = useTranslations()

  const activeBaskets = useMemo(
    () => baskets.filter(basket => basket.status === 'active'),
    [baskets],
  )

  const sortedBaskets = useMemo(
    () =>
      sortBy(activeBaskets, [
        basket =>
          basket.price_change?.period === BASKET_PERIOD
            ? Number(basket.price_change.percentage) || 0
            : -Infinity,
      ]).reverse(),
    [activeBaskets],
  )

  const getPeriodChange = (basket: CryptoBasket) =>
    basket.price_change?.period === BASKET_PERIOD ? (
      <PriceChange value={Number(basket.price_change.percentage) * 0.01} />
    ) : (
      NOT_APPLICABLE
    )

  const columns = useMemo(
    () => [
      {
        accessor: 'basket',
        title: t('baskets.basket'),
        textAlign: 'left' as const,
        // No inline width: the desktop cap (340px) lives in the scoped
        // style.module.css rule, aligned with the stocks table (WWW-5144).
        render: (basket: CryptoBasket) => (
          <div className={styles.mobileBasketRow}>
            <Group wrap="nowrap" gap={8} align="center" style={{ flex: 1, minWidth: 0 }}>
              <Box className={styles.basketImage}>
                <NextBaseImage
                  src={basket.icon_url}
                  width="100%"
                  height="100%"
                  radius="inherit"
                  alt={basket.title}
                />
              </Box>
              <Text
                variant="body"
                c="var(--content-primary)"
                className={styles.desktopBasketTitle}
                style={{
                  flex: 1,
                  wordBreak: 'break-word',
                  minWidth: 0,
                }}
              >
                {basket.title}
              </Text>
              <div className={styles.mobileBasketInfo}>
                <Text variant="body" c="var(--content-primary)" style={{ wordBreak: 'break-word' }}>
                  {basket.title}
                </Text>
                <span className={styles.mobileCoinsText}>
                  {t('baskets.coinCount', { count: basket.coins.length })}
                </span>
              </div>
            </Group>
            <div className={sharedStyles.mobilePriceChange}>{getPeriodChange(basket)}</div>
          </div>
        ),
      },
      {
        accessor: 'coins',
        title: t('globalMetrics.coins'),
        // Centered to match the stocks basket table's middle column (WWW-5144).
        textAlign: 'center' as const,
        render: (basket: CryptoBasket) => <CoinsColumn basket={basket} />,
      },
      {
        accessor: `period-${BASKET_PERIOD}`,
        title: t('baskets.periods.3Years'),
        textAlign: 'right' as const,
        render: (basket: CryptoBasket) => getPeriodChange(basket),
      },
      {
        accessor: 'action',
        title: '',
        textAlign: 'center' as const,
        width: '0%',
        // Mirrors the stocks basket table (WWW-5144): both the Trade link and a
        // whole-row click go to the same cdc-web signup URL.
        render: () => <TradeColumn />,
      },
    ],
    [t],
  )

  if (activeBaskets.length === 0) {
    return null
  }

  return (
    // The extra `basketsRoot` class scopes the WWW-5144 basket-specific rules
    // (desktop: row metrics + column widths aligned with the stocks basket
    // table; mobile: hide the new Trade column) without touching the shared
    // style.module.css, which also styles the CoinsTable on the all-coins tab.
    <Box pos="relative" className={styles.basketsRoot}>
      <DataTable
        textSelectionDisabled
        records={sortedBaskets}
        columns={columns}
        idAccessor="id"
        defaultColumnProps={{
          titleClassName: 'typography-body',
          cellsClassName: `typography-body-number ${sharedStyles.tableCell}`,
        }}
        classNames={sharedStyles}
        pinFirstColumn
        onRowClick={gotoCdcWebSignupPage}
        minHeight="500px"
        backgroundColor="var(--bg-app)"
        withTableBorder={false}
        withColumnBorders={false}
        withRowBorders={false}
        highlightOnHover
        highlightOnHoverColor="var(--bg-surface-secondary)"
      />
    </Box>
  )
}
